import locationSvg from 'client/images/icons/icon_location.svg';
import buildingSvg from 'client/images/icons/icon_building.svg';
import geolocatedSvg from 'client/images/icons/geolocated_icon.svg';
import searchSvg from 'client/images/icons/search.svg';
import { get, sortBy, uniqBy } from '../util/utils';

export const HIT_TYPE_BUILDING = 'building'; // building result
export const HIT_TYPE_GEOGROUPING = 'geogrouping'; // market result
export const HIT_TYPE_GEOLOCATED = 'geolocated'; // first nearby location
export const HIT_TYPE_SUGGESTED = 'suggested'; // rest of nearby locations shown from getClosestMarketsWithType

export function getHitType(hit) {
  if (!hit) {
    return null;
  }

  if (hit.hitType) {
    return hit.hitType;
  }

  if (hit.address) {
    return HIT_TYPE_BUILDING;
  }

  if (hit.slug) {
    return HIT_TYPE_GEOGROUPING;
  }

  return HIT_TYPE_GEOGROUPING;
}

export function getHitSVG(iconType) {
  switch (iconType) {
    case HIT_TYPE_BUILDING:
      return buildingSvg;

    case HIT_TYPE_GEOGROUPING:
      return locationSvg;

    case HIT_TYPE_GEOLOCATED:
      return geolocatedSvg;

    default:
      return searchSvg;
  }
}

export function getHitName(hit, tryHighlightedResult, locale) {
  if (tryHighlightedResult) {
    const htmlName = locale
      ? get(hit, `_highlightResult.name_${locale}.value`)
      : get(hit, '_highlightResult.name.value');
    if (htmlName) {
      return htmlName;
    }
  }

  if (locale) {
    const localizedName = get(hit, `name_${locale}`);
    if (localizedName) {
      return localizedName;
    }
  }

  return get(hit, 'name');
}

export function getHitParentName(hit, tryHighlightedResult, locale) {
  if (tryHighlightedResult) {
    const htmlParent =
      (locale
        ? get(hit, `_highlightResult.parent.name_${locale}.value`) ||
          get(hit, `_highlightResult.marketgeo.name_${locale}.value`)
        : get(hit, '_highlightResult.parent.name.value')) || get(hit, '_highlightResult.marketgeo.name.value');
    if (htmlParent) {
      return htmlParent;
    }
  }

  if (locale) {
    const localizedName = get(hit, `parent.name_${locale}`) || get(hit, `marketgeo.name_${locale}`);
    if (localizedName) {
      return localizedName;
    }
  }

  return get(hit, 'parent.name') || get(hit, 'marketgeo.name');
}

export function getHitLabel(hit, tryHighlightedResult, locale) {
  const sanitizedLocale = locale && locale !== 'en-US' ? locale : null;
  const labelName = getHitName(hit, tryHighlightedResult, sanitizedLocale);
  const parentName = getHitParentName(hit, tryHighlightedResult, sanitizedLocale);

  return parentName ? `${labelName}, ${parentName}` : labelName;
}

export function getMapboxFeatureName(feature, locale) {
  return get(feature, `place_name_${locale}`) || get(feature, 'place_name');
}

export function flattenAndUniqueResultsByID({ exactHits, geolocatedHits }) {
  const exactHitsFlattened = get(exactHits, 'results', []);
  const geoHitsFlattened = get(geolocatedHits, 'results', []);
  const searchResults = [...exactHitsFlattened, ...geoHitsFlattened];
  const hitsMayHaveDupes = searchResults.reduce((reducer, result) => {
    return [...reducer, ...result.hits];
  }, []);
  return uniqBy(hitsMayHaveDupes, hit => hit.id);
}

const rankByWords = hit => -1 * get(hit, '_rankingInfo.words');
const rankByPromityDistance = hit => get(hit, '_rankingInfo.proximityDistance');

export function sortHits(hits) {
  return sortBy(hits, [rankByWords, rankByPromityDistance]);
}

export function normalizeHits({ exactHits, geolocatedHits }) {
  return sortHits(flattenAndUniqueResultsByID({ exactHits, geolocatedHits }));
}

export const getLocationUrl = ({ selection, defaultUrl }) => {
  const locationSlug = get(selection, 'slug');
  if (locationSlug) {
    if (get(selection, 'address')) {
      return `/buildings/${locationSlug}`;
    }

    return `/l/${locationSlug}`;
  }

  return defaultUrl;
};
